import type { IFilterValues } from '@pages/reports/types/filter-values.interface';
import type { IReportConfiguration } from '@pages/reports/types/report-config.interface';

/** Additional filter API params: omit key when empty (null, undefined, [], or date range with no bounds). */
export function isAdditionalFilterValueEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'object' && value !== null) {
        const record = value as Record<string, unknown>;
        if ('from' in record || 'to' in record) {
            const from = record['from'];
            const to = record['to'];
            const hasFrom = from != null && from !== '';
            const hasTo = to != null && to !== '';
            if ('includeEmpty' in record) {
                const includeEmpty = record['includeEmpty'];
                return !hasFrom && !hasTo && includeEmpty !== true;
            }
            return !hasFrom && !hasTo;
        }
    }
    return false;
}

export function buildReportFilterParams(
    values: IFilterValues,
    config: IReportConfiguration<unknown>
): Record<string, unknown> {
    const params: Record<string, unknown> = {};
    const filterCategories = ['main', 'additional'] as const;

    for (const category of filterCategories) {
        const filters = config.filters[category];
        if (!filters) continue;

        for (const filter of filters) {
            if (filter.mapping) {
                Object.keys(filter.mapping).forEach((key) => {
                    const path = filter.mapping![key];
                    const raw = getValueByPath(values, path);
                    if (category === 'main') {
                        params[key] = raw ?? null;
                    } else if (!isAdditionalFilterValueEmpty(raw)) {
                        params[key] = raw as unknown;
                    }
                });
            } else if (filter.fieldName) {
                const field = filter.fieldName;
                const raw = (values as Record<string, unknown>)[field];
                if (category === 'main') {
                    params[field] = raw ?? null;
                } else if (!isAdditionalFilterValueEmpty(raw)) {
                    params[field] = raw;
                }
            }
        }
    }

    return params;
}

function getValueByPath(obj: IFilterValues, path: string): unknown {
    const keys = path.split('.');
    let value: unknown = obj as Record<string, unknown>;
    for (const key of keys) {
        if (value && typeof value === 'object' && key in (value as Record<string, unknown>)) {
            value = (value as Record<string, unknown>)[key];
        } else {
            return undefined;
        }
    }
    return value;
}
